import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/store/hooks";
import { loginSuccess } from "@/features/auth/authSlice";
import { useLoginMutation } from "@/features/auth/authApi";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import toast from "react-hot-toast";
import Layout from "@/components/Layout/Layout";

interface LoginForm {
  email: string;
  password: string;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      // Send only email & password
      const result = await login({ email: data.email, password: data.password }).unwrap();
      console.log("Login API Response:", result);

      const { user, token, refreshToken, tenant } = result.data;

      dispatch(loginSuccess({ user, token, refreshToken, tenant }));

      localStorage.setItem("token", result.data.token);
      localStorage.setItem("refreshToken", result.data.refreshToken);

      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Login API Error:", error);
      const errorMessage = error?.data?.message || error?.data?.error || "Login failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <Layout hideChrome>
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow">
          <div className="text-center">
            {/* <img
              src="http://16.171.55.186/DabraClasses/images/1745219206IMG_3120.JPG"
              alt="Vedemy Admin"
              style={{ width: "120px" }}
            /> */}

            <img src={'https://nocache-appxdb-v2.classx.co.in/subject/2024-12-24-0.7611780787393996.jpg'} alt="Admin" className="header-brand-img light-logo1" style={{ width: '65px' }} />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Admin Login</h2>
            <p className="mt-1 text-sm text-gray-600">Sign in to continue</p>
          </div>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit(onSubmit)}>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  {...register("email", { required: "Email is required" })}
                  type="email"
                  autoComplete="email"
                  className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-[30px]![important]"
                  placeholder="Enter your email"
                  style={{
                    paddingLeft: "35px"
                  }}
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  {...register("password", { required: "Password is required" })}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your password"
                  style={{
                    paddingLeft: "35px"
                  }}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
            </div>

            {/* Remember me & Forgot */}
            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <span className="text-gray-700">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-blue-600 hover:text-blue-500">Forgot password?</Link>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Signing in..." : "Login"}
            </button>

            {/* Register link */}
            <p className="text-center text-sm text-gray-600">
              Don’t have an account? <Link to="/register" className="text-blue-600 hover:text-blue-500">Sign up</Link>
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
